import { GODIA_LOYALTY } from "@constants";

import React from "react";

import {
    setIsCustomerBenefitOpen,
    setIsGlobalSummaryOrderOpened,
    setIsOrderTakingPreviousStep,
} from "@pages/Kiosk/store";

/**
 * Encapsulates the "open the order summary" navigation that both the standard
 * basket (BasketActionButtons) and the PRM basket (PrmBasket) trigger, so the
 * business logic no longer lives — and drifts — inside two View components.
 *
 * Returns a STABLE `handleOrderSummaryOnClickEvent` callback (safe to pass to
 * usePostAdditionalSaleNavigation, which keeps it in a ref). The latest values
 * are read through a ref, so the callback's identity never changes across
 * renders/toggles and the navigation effect won't re-fire spuriously.
 *
 * Legitimate PRM vs standard difference: the standard basket additionally
 * raises the global summary flag and marks the order-taking previous step
 * (`setIsGlobalSummaryOrderOpened` / `setIsOrderTakingPreviousStep`). PRM does
 * not — those globals are driven elsewhere in PRM mode. Pass
 * `syncGlobalSummary: true` for the standard basket, omit it for PRM.
 *
 * Pass `syncGlobalSummary: true` for the standard basket, omit it for PRM.
 */
export interface UseOrderSummaryNavigationParams {
    /** Functional state updater for the local summary-open flag. */
    setIsSummaryOrderOpened: (updater: (prev: boolean) => boolean) => void;
    /** Current local summary-open flag value. */
    isSummaryOrderOpened: boolean;
    /** Loyalty program id (numeric enum, e.g. GODIA_LOYALTY = 4). */
    fidelityType: number;
    autoLaunchOnPayButton: boolean;
    giftsActive: boolean;
    rewardsActive: boolean;
    /** Standard basket only — raises the global summary / previous-step flags. */
    syncGlobalSummary?: boolean;
}

export function useOrderSummaryNavigation({
    setIsSummaryOrderOpened,
    isSummaryOrderOpened,
    fidelityType,
    autoLaunchOnPayButton,
    giftsActive,
    rewardsActive,
    syncGlobalSummary = false,
}: UseOrderSummaryNavigationParams): () => void {
    // Mirror the latest inputs into a ref so the returned callback can stay
    // referentially stable while always reading fresh values.
    const latest = React.useRef<UseOrderSummaryNavigationParams>({
        setIsSummaryOrderOpened,
        isSummaryOrderOpened,
        fidelityType,
        autoLaunchOnPayButton,
        giftsActive,
        rewardsActive,
        syncGlobalSummary,
    });
    latest.current = {
        setIsSummaryOrderOpened,
        isSummaryOrderOpened,
        fidelityType,
        autoLaunchOnPayButton,
        giftsActive,
        rewardsActive,
        syncGlobalSummary,
    };

    return React.useCallback(() => {
        const {
            setIsSummaryOrderOpened: setOpen,
            isSummaryOrderOpened: wasOpened,
            fidelityType: fidelity,
            autoLaunchOnPayButton: autoLaunch,
            giftsActive: gifts,
            rewardsActive: rewards,
            syncGlobalSummary: syncGlobal,
        } = latest.current;

        setOpen((prev) => !prev);

        if (syncGlobal) {
            setIsGlobalSummaryOrderOpened(true);
            setIsOrderTakingPreviousStep(true);
        }

        if (
            fidelity === GODIA_LOYALTY &&
            !wasOpened &&
            autoLaunch &&
            (gifts || rewards)
        ) {
            setIsCustomerBenefitOpen(true);
        }
        // Empty deps on purpose: all volatile inputs are read through `latest`,
        // so this callback is intentionally stable for its whole lifetime.
    }, []);
}

export default useOrderSummaryNavigation;
